<!-- 工作台首页轮播图配置；未配置时首页回落内置 5 张默认图 -->
<template>
  <ContentWrap>
    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="mb-12px"
      title="配置工作台首页轮播。启用后按排序展示；若当前租户未配置任何启用项，首页自动展示系统默认 5 张。"
    />
    <el-form :inline="true" :model="queryParams" class="-mb-15px" @submit.prevent>
      <el-form-item label="标题">
        <el-input
          v-model="queryParams.title"
          clearable
          placeholder="标题关键字"
          class="!w-180px"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="queryParams.status" clearable placeholder="全部" class="!w-120px">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">
          <Icon icon="ep:search" />搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon icon="ep:refresh" />重置
        </el-button>
        <el-button
          v-hasPermi="['portal:banner:create']"
          type="success"
          plain
          @click="openForm()"
        >
          <Icon icon="ep:plus" />新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="80" align="center" />
      <el-table-column label="图片" width="180" align="center">
        <template #default="{ row }">
          <el-image
            v-if="row.imageUrl"
            :src="row.imageUrl"
            fit="cover"
            class="banner-thumb"
            :preview-src-list="[row.imageUrl]"
            preview-teleported
          />
          <span v-else class="text-gray-400 text-12px">未上传</span>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
      <el-table-column prop="sort" label="排序" width="80" align="center" />
      <el-table-column label="状态" width="100" align="center">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.COMMON_STATUS" :value="row.status" />
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
      <el-table-column
        prop="createTime"
        label="创建时间"
        width="170"
        align="center"
        :formatter="dateFormatter"
      />
      <el-table-column label="操作" width="160" fixed="right" align="center">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['portal:banner:update']"
            link
            type="primary"
            @click="openForm(row)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['portal:banner:delete']"
            link
            type="danger"
            @click="handleDelete(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="formVisible" :title="formData.id ? '编辑轮播图' : '新增轮播图'" width="560px">
    <el-form ref="formRef" :model="formData" :rules="formRules" label-width="90px">
      <el-form-item label="标题" prop="title">
        <el-input v-model="formData.title" maxlength="64" show-word-limit placeholder="可选" />
      </el-form-item>
      <el-form-item label="图片" prop="imageUrl">
        <UploadImg v-model="formData.imageUrl" :limit="1" directory="system/workbench-banner" />
        <div class="text-12px text-gray-400 mt-8px">建议宽高比约 16:9，与工作台轮播区域匹配</div>
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="formData.sort" :min="0" :max="999" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="备注">
        <el-input v-model="formData.remark" type="textarea" :rows="2" maxlength="200" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      <el-button @click="formVisible = false">取消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { BannerApi, type BannerVO } from '@/api/system/banner'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'SystemWorkbenchBanner' })

const message = useMessage()
const { t } = useI18n()

const loading = ref(false)
const saving = ref(false)
const list = ref<BannerVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  title: undefined as string | undefined,
  status: undefined as number | undefined
})

const getList = async () => {
  loading.value = true
  try {
    const data = await BannerApi.getList(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryParams.title = undefined
  queryParams.status = undefined
  handleQuery()
}

const formVisible = ref(false)
const formRef = ref<FormInstance>()
const formData = reactive<BannerVO>({
  id: undefined,
  title: '',
  imageUrl: '',
  sort: 0,
  status: 0,
  remark: ''
})
const formRules: FormRules = {
  imageUrl: [{ required: true, message: '请上传轮播图片', trigger: 'change' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

const resetForm = () => {
  formData.id = undefined
  formData.title = ''
  formData.imageUrl = ''
  formData.sort = 0
  formData.status = 0
  formData.remark = ''
}

const openForm = (row?: BannerVO) => {
  resetForm()
  if (row) {
    Object.assign(formData, {
      id: row.id,
      title: row.title || '',
      imageUrl: row.imageUrl || '',
      sort: row.sort ?? 0,
      status: row.status ?? 0,
      remark: row.remark || ''
    })
  }
  formVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    const payload: BannerVO = {
      id: formData.id,
      title: formData.title,
      imageUrl: formData.imageUrl,
      sort: formData.sort ?? 0,
      status: formData.status,
      remark: formData.remark
    }
    if (payload.id) {
      await BannerApi.update(payload)
      message.success(t('common.updateSuccess'))
    } else {
      await BannerApi.create(payload)
      message.success(t('common.createSuccess'))
    }
    formVisible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}

const handleDelete = async (id?: number) => {
  if (!id) return
  try {
    await message.delConfirm()
    await BannerApi.delete(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

onMounted(getList)
</script>

<style scoped>
.banner-thumb {
  width: 148px;
  height: 68px;
  border-radius: 6px;
}
</style>
